import { Link, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback } from 'react';

import { ProductImage } from './ProductImage';
import { PRODUCT_PRICE_DIFF, PRODUCT_TILE_LINK, PRODUCT_TILE_ROOT, PRODUCT_TILE_TITLE } from './constants';

const ProductTileRoot = styled('div')<{ ownerState: { isLarge: boolean } }>(({ ownerState, theme }) => ({
  boxSizing: 'border-box',
  color: theme.palette.text.disabled,
  height: 113,
  lineHeight: theme.typography.pxToRem(18),

  ...(ownerState.isLarge && {
    flexBasis: '40%',
    fontSize: theme.typography.caption.fontSize,
    justifyContent: 'center',
    lineHeight: theme.typography.pxToRem(18),
    margin: theme.spacing(0, '5%', 3),
    textAlign: 'center',
  }),

  [theme.breakpoints.up('md')]: {
    ...(ownerState.isLarge && {
      flexBasis: 'unset',
      height: 148,
      margin: theme.spacing(12, '4%', 0),
      width: '17%',
    }),
  },
}));

const ProductTileProductLink = styled(Link)<{ ownerState: { isLarge: boolean } }>(({ ownerState, theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  textDecoration: 'none',

  ...(ownerState.isLarge && {
    background: theme.palette.common.white,
  }),

  '&:hover, &:focus, &:active': {
    color: theme.palette.grey[600],
    WebkitTapHighlightColor: 'transparent',
  },
}));

const ProductTileTitle = styled('span')<{ ownerState: { isLarge: boolean } }>(({ ownerState, theme }) => ({
  ...(ownerState.isLarge && {
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  }),

  [theme.breakpoints.up('md')]: {
    '&:hover, &:focus, &:active': {
      ...(ownerState.isLarge && {
        color: theme.palette.text.primary,
      }),
    },
  },
}));

const ProductTilePriceBox = styled('div')<{ ownerState: { isLarge: boolean } }>(({ ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',

  ...(ownerState.isLarge && {
    alignItems: 'stretch',
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  }),
}));
const ProductTileActualPrice = styled('span')<{ ownerState: { isLarge: boolean } }>(({ ownerState, theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginTop: theme.spacing(2),

  ...(ownerState.isLarge && {
    margin: 0,
  }),
}));

const ProductTileStandardPrice = styled('span')<{ ownerState: { isLarge: boolean } }>(({ ownerState, theme }) => ({
  textDecoration: 'line-through',

  ...(ownerState.isLarge && {
    color: theme.palette.text.disabled,
  }),
}));

export interface ProductTileProps {
  basicCataloguePrice?: string;
  currentPrice?: string;
  displayProductName?: boolean;
  imageUrl?: string;
  largeVersion?: boolean;
  position: number;
  productCode?: string;
  productName?: string;
  onClick: (position: number) => void;
}

export const ProductTile: FC<ProductTileProps> = ({
  basicCataloguePrice,
  currentPrice,
  displayProductName = false,
  imageUrl,
  largeVersion = false,
  position,
  productCode,
  productName,
  onClick,
}) => {
  const productUrl = `/products/product?code=${productCode ?? ''}`;
  const ownerState = { isLarge: largeVersion };
  const { getTestId } = useTestId();

  const onClickHandler = useCallback(() => {
    onClick(Number(position));
  }, [onClick, position]);

  return (
    <ProductTileRoot
      ownerState={ownerState}
      data-testid={getTestId(PRODUCT_TILE_ROOT)}
    >
      <ProductTileProductLink
        data-testid={getTestId(PRODUCT_TILE_LINK)}
        ownerState={ownerState}
        href={productUrl}
        title={productName}
        onClick={onClickHandler}
      >
        <ProductImage
          image={imageUrl ?? ''}
          name={productName ?? ''}
          size={largeVersion ? '' : 'small'}
        />
        {!!displayProductName && (
          <ProductTileTitle
            data-testid={getTestId(PRODUCT_TILE_TITLE)}
            ownerState={ownerState}
          >
            {productName}
          </ProductTileTitle>
        )}
        <ProductTilePriceBox ownerState={ownerState}>
          <ProductTileActualPrice ownerState={ownerState}>{currentPrice}</ProductTileActualPrice>
          {currentPrice !== basicCataloguePrice && (
            <ProductTileStandardPrice
              data-testid={getTestId(PRODUCT_PRICE_DIFF)}
              ownerState={ownerState}
            >
              {basicCataloguePrice}
            </ProductTileStandardPrice>
          )}
        </ProductTilePriceBox>
      </ProductTileProductLink>
    </ProductTileRoot>
  );
};
